// eslint-disable-next-line no-restricted-imports
import type { Components as ReactMarkdownComponents } from 'react-markdown';
import { ComponentProps, lazy, Suspense, useMemo } from 'react';
import { Paragraph, Heading } from '@alto-avios/alto-ui';

export type Components = ReactMarkdownComponents;

export interface MarkdownProps {
  components?: Partial<Components>;
  /**
   * When provided this will override the foregroundColor for all <p> and <h1>/<h2>/<h3> e.t.c. text components
   */
  overrideForegroundColor?: ComponentProps<typeof Paragraph>['foregroundColor'];
  children: string | null | undefined;
}

const ReactMarkdown = lazy(() => import('react-markdown'));

export const Markdown = ({ children, components = {}, overrideForegroundColor }: MarkdownProps) => {
  const mergedComponents = useMemo((): Components => {
    return {
      p: ({ children }) => <Paragraph foregroundColor={overrideForegroundColor}>{children}</Paragraph>,
      h1: ({ children }) => (
        <Heading as="h1" foregroundColor={overrideForegroundColor}>
          {children}
        </Heading>
      ),
      h2: ({ children }) => (
        <Heading as="h2" foregroundColor={overrideForegroundColor}>
          {children}
        </Heading>
      ),
      h3: ({ children }) => (
        <Heading as="h3" foregroundColor={overrideForegroundColor}>
          {children}
        </Heading>
      ),
      h4: ({ children }) => (
        <Heading as="h4" foregroundColor={overrideForegroundColor}>
          {children}
        </Heading>
      ),
      h5: ({ children }) => (
        <Heading as="h5" foregroundColor={overrideForegroundColor}>
          {children}
        </Heading>
      ),
      h6: ({ children }) => (
        <Heading as="h6" foregroundColor={overrideForegroundColor}>
          {children}
        </Heading>
      ),
      ...components,
    };
  }, [components, overrideForegroundColor]);

  return (
    <Suspense fallback={null}>
      <ReactMarkdown components={mergedComponents}>{children}</ReactMarkdown>
    </Suspense>
  );
};
